import React, {useState, useEffect, useRef, useMemo} from 'react';
import {
    FormControl,
    InputLabel,
    OutlinedInput,
    FormHelperText,
    Box,
    Typography,
    InputAdornment,
    InputBaseComponentProps,
} from '@mui/material';
import {getDatePickerFieldSx} from "../../util/style";
import {Tooltip} from "./Tooltip";

const DateInputContainer = React.forwardRef<HTMLDivElement, InputBaseComponentProps>(
    function DateInputContainer(props, ref) {
        const {children, ...other} = props;
        return (
            <Box ref={ref} {...other} sx={{
                display: 'flex',
                alignItems: 'center',
                fontFamily: 'Consolas, "Courier New", monospace'
            }}>
                {children}
            </Box>
        );
    }
);

interface DateInputPartProps {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    placeholder: string;
    maxLength: number;
    inputRef: React.Ref<HTMLInputElement>;
    name: string;
    onMouseDown?: (event: React.MouseEvent<HTMLInputElement>) => void;
}

const DateInputPart: React.FC<DateInputPartProps> = ({
                                                         value,
                                                         onChange,
                                                         onKeyDown,
                                                         placeholder,
                                                         maxLength,
                                                         inputRef,
                                                         name,
                                                         onMouseDown
                                                     }) => (
    <div style={{display: 'inline-block'}}>
        <input
            ref={inputRef}
            type="tel"
            value={value}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            maxLength={maxLength}
            name={name}
            onMouseDown={onMouseDown}
            style={{
                width: `calc(${maxLength}ch + ${value.length === maxLength ? "6px" : "10px"})`,
                border: 'none',
                background: 'transparent',
                outline: 'none',
                textAlign: 'right',
                font: 'inherit',
                color: 'inherit',
                paddingRight: '2px',
            }}
        />
    </div>
);

export type DateInputFieldProps = {
    className?: string;
    label: string;
    name: string;
    defaultValue?: string;
    handleValueChange?: (value: any, field?: string) => void,
    handleValidation?: (value: any, field?: string) => void | boolean,
    error?: string | undefined;
    disabled?: boolean;
    info?: string;
};

export const getPaddedValue = (unpaddedValue: string | null): string => {
    if (!unpaddedValue) return '';
    const [day, month, year] = unpaddedValue.split('.');
    const paddedDay = day && day.length === 1 ? `0${day}` : day || '';
    const paddedMonth = month && month.length === 1 ? `0${month}` : month || '';
    const finalYear = year || '';
    if (!paddedDay && !paddedMonth && !finalYear) return '';
    return `${paddedDay}.${paddedMonth}.${finalYear}.`;
};

export const DateInputField: React.FC<DateInputFieldProps> = ({
                                                                  className,
                                                                  label,
                                                                  name,
                                                                  defaultValue = '',
                                                                  handleValueChange = () => {
                                                                  },
                                                                  handleValidation = () => {
                                                                  },
                                                                  error,
                                                                  disabled = false,
                                                                  info,
                                                              }) => {
    const getPartsFromValue = (v: string) => {
        if (typeof v !== 'string') return {day: '', month: '', year: ''};
        const valueParts = v.split('.');
        return {day: valueParts[0] || '', month: valueParts[1] || '', year: valueParts[2] || ''};
    };

    const [parts, setParts] = useState(getPartsFromValue(defaultValue));
    const [uiValue, setUiValue] = useState(defaultValue);
    const [isFocused, setIsFocused] = useState(false);

    const dayRef = useRef<HTMLInputElement>(null);
    const monthRef = useRef<HTMLInputElement>(null);
    const yearRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setParts(getPartsFromValue(defaultValue));
    }, [defaultValue]);

    useEffect(() => {
        const {day, month, year} = parts;
        if (!day && !month && !year) {
            if (uiValue !== '') setUiValue('');
        } else {
            const assembledValue = `${day}.${month}.${year}.`;
            if (assembledValue !== uiValue) setUiValue(assembledValue);
        }
    }, [parts, uiValue]);

    useEffect(() => {
        if (uiValue !== defaultValue) {
            const handler = setTimeout(() => {
                handleValueChange(uiValue, name);
                const isDateComplete = parts.day.length === 2 && parts.month.length === 2 && parts.year.length === 4;
                if (isDateComplete) {
                    const dataValue = getPaddedValue(uiValue);
                    handleValidation(dataValue, name);
                }
            }, 10);
            return () => clearTimeout(handler);
        }
    }, [uiValue]);

    const createPartChangeHandler = (partName: 'day' | 'month' | 'year', nextFieldRef?: React.RefObject<HTMLInputElement | null>) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value.replace(/[^0-9]/g, '');
        const maxLength = partName === 'year' ? 4 : 2;
        if (val.length <= maxLength) {
            setParts(prev => ({...prev, [partName]: val}));
            if (val.length === maxLength && nextFieldRef?.current) {
                nextFieldRef.current.focus();
            }
        }
    };

    const createKeyDownHandler = (prevFieldRef?: React.RefObject<HTMLInputElement | null>) => (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && e.currentTarget.value === '' && prevFieldRef?.current) {
            prevFieldRef.current.focus();
        }
    };

    const handleFocus = () => setIsFocused(true);
    const onBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (!(containerRef.current as HTMLDivElement | null)?.contains(e.relatedTarget as Node)) {
            setIsFocused(false);
            const dataValue = getPaddedValue(uiValue);
            handleValidation(dataValue, name);
        }
    };

    const handleContainerMouseDown = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        if (!isFocused && dayRef.current) {
            dayRef.current.focus();
        }
    };

    const handlePartMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (isFocused) {
            e.currentTarget.focus();
        } else if (dayRef.current) {
            dayRef.current.focus();
        }
    };

    const isShrunk = isFocused || !!(parts.day || parts.month || parts.year);
    const sx = useMemo(() => getDatePickerFieldSx(name, error), [name, error]);

    return (
        <div className={className}>
            <FormControl variant="outlined" sx={sx} error={!!error} required>
                <InputLabel htmlFor={name} shrink={isShrunk} required={false}>{label}</InputLabel>
                <OutlinedInput
                    id={name}
                    label={label}
                    disabled={disabled}
                    onFocus={handleFocus}
                    onBlur={onBlur}
                    ref={containerRef}
                    notched={isShrunk}
                    endAdornment={info ? (<InputAdornment position="end" sx={{mr: 1, zIndex: 1}}><Tooltip text={info}/></InputAdornment>) : null}
                    inputComponent={DateInputContainer}
                    inputProps={{
                        onMouseDown: handleContainerMouseDown,
                        children: (
                            <>
                                <DateInputPart onMouseDown={handlePartMouseDown} inputRef={dayRef} value={parts.day}
                                               onChange={createPartChangeHandler('day', monthRef)}
                                               onKeyDown={createKeyDownHandler()} placeholder={isShrunk ? 'DD' : ''}
                                               maxLength={2} name={`${name}-day`}/>
                                <Typography onMouseDown={handleContainerMouseDown} variant="body1" component="span"
                                            sx={{
                                                color: 'text.secondary',
                                                opacity: isShrunk ? 1 : 0,
                                            }}>.</Typography>
                                <DateInputPart onMouseDown={handlePartMouseDown} inputRef={monthRef} value={parts.month}
                                               onChange={createPartChangeHandler('month', yearRef)}
                                               onKeyDown={createKeyDownHandler(dayRef)}
                                               placeholder={isShrunk ? 'MM' : ''} maxLength={2} name={`${name}-month`}/>
                                <Typography onMouseDown={handleContainerMouseDown} variant="body1" component="span"
                                            sx={{
                                                color: 'text.secondary',
                                                opacity: isShrunk ? 1 : 0,
                                            }}>.</Typography>
                                <DateInputPart onMouseDown={handlePartMouseDown} inputRef={yearRef} value={parts.year}
                                               onChange={createPartChangeHandler('year')}
                                               onKeyDown={createKeyDownHandler(monthRef)}
                                               placeholder={isShrunk ? 'YYYY' : ''} maxLength={4}
                                               name={`${name}-year`}/>
                                <Typography onMouseDown={handleContainerMouseDown} variant="body1" component="span"
                                            sx={{
                                                color: 'text.secondary',
                                                opacity: isShrunk ? 1 : 0,
                                            }}>.</Typography>
                            </>
                        )
                    }}
                />
                {!!error && <FormHelperText>{error}</FormHelperText>}
            </FormControl>
        </div>
    );
};
